import { IonReorder, IonRouterLink } from '@ionic/react';
import { useCallback, useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { formatPhoneNumber } from 'react-phone-number-input';
import { useHistory } from 'react-router';

import ActionIconButton from '@components/buttons/ActionIconButton';
import Dot from '@components/Dot';
import PinToggle from '@components/PinToggle';
import styles from '@components/Resource.module.css';
import ResourceFeatures from '@components/ResourceFeatures';
import Icon from '@components/text/Icon';
import StatusText from '@components/text/StatusText';
import TextWithIcon from '@components/text/TextWithIcon';
import BodyCopy from '@components/typography/BodyCopy';
import Heading2 from '@components/typography/Heading2';
import Hyperlink from '@components/typography/Hyperlink';
import { openerContext } from '@context/OpenerStore';
import useMutationWithValidation from '@hooks/useMutationWithValidation';
import useMyExpert from '@hooks/useMyExpert';
import useSanitizeHtml from '@hooks/useSanitizeHtml';
import {
  PinResourceTypeEnum,
  ResourceFragment as ResourceType,
  ResourceUpdateDocument,
  ResourceUpdateMutation,
  ResourceUpdateMutationVariables
} from '@typing/Generated';
import { Status } from '@typing/Models';
import { OpenerType } from '@typing/State';
import { isMobileWeb, isNativeApp } from '@utils/platformUtils';
import { journeyRoute } from '@utils/routeUtils';

type Props = {
  carePlanPublishedAt: Date | null;
  journeyId: string;
  onDelete: (resource: ResourceType) => void;
  onEdit: (resource: ResourceType) => void;
  resource: ResourceType;
};

const Resource = ({ journeyId, onDelete, onEdit, resource }: Props) => {
  const myExpert = useMyExpert();
  const history = useHistory();
  const intl = useIntl();

  const [updateResource] = useMutationWithValidation<ResourceUpdateMutation, ResourceUpdateMutationVariables>(
    ResourceUpdateDocument,
    'resource'
  );
  const { toggleOpener } = useContext(openerContext);

  const draft = !resource.task.draft && resource.draft;

  const handleEdit = useCallback(() => {
    onEdit(resource);
  }, [resource, onEdit]);

  const handleDelete = useCallback(() => {
    onDelete(resource);
  }, [resource, onDelete]);

  const updateUseful = useCallback(
    (useful: boolean) => {
      updateResource({
        id: resource.id,
        useful
      });
    },
    [resource.id, updateResource]
  );

  const handleMessageExpert = useCallback(
    (resource: ResourceType) => {
      const isMobile = isMobileWeb() || isNativeApp();
      toggleOpener(OpenerType.MESSAGES, !isMobile, resource);

      if (isMobile) {
        history.push(
          journeyRoute({
            journeyId,
            linkAfterJourney: `/messages`,
            myExpert
          })
        );
      }
    },
    [history, journeyId, myExpert, toggleOpener]
  );

  const sanitizedDescription = useSanitizeHtml(resource.description, journeyId, resource);

  return (
    <div className={`${styles.resource}`} data-test="resource">
      {draft && (
        <div className={styles.draft}>
          <StatusText status={Status.DRAFT} />
        </div>
      )}
      <div className={styles.titleAndControls}>
        <Heading2 color="primary700">{resource.title}</Heading2>
        <div className={styles.controlsRow}>
          {!draft && (
            <>
              <div className={styles.control}>
                <Dot unread={resource.ownerViewedAt === null} />
              </div>
              <div className={styles.control}>
                <PinToggle
                  color="neutral500"
                  journeyId={journeyId}
                  resourceId={resource.id}
                  resourceType={PinResourceTypeEnum.RESOURCE}
                />
              </div>
            </>
          )}
          {myExpert && (
            <>
              <div className={styles.control}>
                <ActionIconButton ariaLabelKey="accessibility.ariaLabels.edit" icon="edit" onClick={handleEdit} />
              </div>
              <div className={styles.control}>
                <ActionIconButton ariaLabelKey="accessibility.ariaLabels.delete" icon="trash" onClick={handleDelete} />
              </div>
              <div className={styles.controlLessMargin}>
                {resource.useful !== false && <IonReorder slot="start" tabIndex={0} />}
              </div>
            </>
          )}
        </div>
      </div>
      {(resource.linkUrl || resource.cost || resource.phoneNumber) && (
        <div className={styles.icons}>
          <div className={styles.info}>
            {resource.linkUrl && (
              <div className={styles.icon}>
                <div>
                  <Icon ariaLabelKey="accessibility.ariaLabels.link" className={styles.infoIcon} icon="link" />
                </div>
                <div className={styles.content}>
                  <Hyperlink href={resource.linkUrl} rel="noopener noreferrer" target="_blank">
                    {resource.linkTitle}
                  </Hyperlink>
                </div>
              </div>
            )}
            {resource.cost && (
              <div className={styles.icon}>
                <div>
                  <Icon
                    ariaLabelKey="accessibility.ariaLabels.dollarSign"
                    className={styles.infoIcon + ' ' + styles.dollarSign}
                    icon="moneyBill"
                  />
                </div>

                <div className={styles.content}>
                  <BodyCopy color="primary700">{resource.cost}</BodyCopy>
                </div>
              </div>
            )}
            {resource.phoneNumber && (
              <div className={styles.icon}>
                <div>
                  <Icon ariaLabelKey="accessibility.ariaLabels.phone" className={styles.infoIcon} icon="phone" />
                </div>

                <div className={styles.content}>
                  <a href={`tel:${resource.phoneNumber}`}>
                    {formatPhoneNumber(resource.phoneNumber)
                      ? formatPhoneNumber(resource.phoneNumber)
                      : resource.phoneNumber}
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      <div className={styles.description}>{sanitizedDescription}</div>
      <ResourceFeatures resource={resource} />
      <div className="align-right">
        <span className={styles.usefulIcons}>
          <IonRouterLink
            className={`${styles.useful} ${resource.useful === true ? styles.selected : styles.unselected}`}
            data-test="useful"
            onClick={() => updateUseful(true)}
          >
            <TextWithIcon
              icon="thumbsUp"
              iconFirst
              text={intl.formatMessage({ id: 'pages.carePlan.task.resources.useful' })}
            />
          </IonRouterLink>
          <IonRouterLink
            className={resource.useful === false ? styles.selected : styles.unselected}
            data-test="not-useful"
            onClick={() => updateUseful(false)}
          >
            <TextWithIcon
              icon="thumbsDown"
              iconFirst
              text={intl.formatMessage({ id: 'pages.carePlan.task.resources.notUseful' })}
            />
          </IonRouterLink>
        </span>
        {!myExpert && (
          <div className={styles.messageExpertLink}>
            <Hyperlink onClick={() => handleMessageExpert(resource)}>
              <FormattedMessage id="pages.carePlan.task.resources.messageYourExpert" />
            </Hyperlink>
          </div>
        )}
      </div>
    </div>
  );
};

export default Resource;
